import { useCallback, useState } from 'react'
import { fetchWithToken, postWithToken } from './http'
import { CredentialsDTO, CredentialsResponse, ServiceProviderStatus } from 'types/credentials'

interface UseFinchReturn {
  getConnectionStatus: (params: Array<[string, string]>) => Promise<CredentialsDTO>
  isLoadingConnectionStatus: boolean
  handleSessionSuccess: (request: FinchSessionSuccessRequest) => Promise<FinchSessionSuccessResponse>
  isHandlingSessionSuccess: boolean
  getSession: (request: FinchSessionRequest) => Promise<FinchSessionResponse>
  isLoadingSession: boolean
  getReauthenticateSession: (request: FinchReauthenticateSessionRequest) => Promise<FinchReauthenticateSessionResponse>
  isLoadingReauthenticateSession: boolean
  error: Error | null
}

// Request types
export interface FinchSessionRequest {
  rootOrganizationId: string // UUID as string
}

export interface FinchSessionResponse {
  organizationId: string // UUID as string
  organizationName: string // Customer name
  sessionId: string
}

export interface FinchReauthenticateSessionRequest {
  rootOrganizationId: string // UUID as string
}

export interface FinchReauthenticateSessionResponse {
  organizationId: string // UUID as string
  credentialId: string
  sessionId: string
}

export interface FinchSessionSuccessRequest {
  organizationId: string // UUID as string
  organizationName: string // Customer name
  code: string // UUID as string
}

export interface FinchSessionSuccessResponse {
  organizationId: string // UUID as string
  organizationName: string // Customer name
}

export interface FinchReauthorizationSessionSuccessResponse {
  organizationId: string // UUID as string
  organizationName: string // Customer name
}

export interface FinchConnectionStatusResponse {
  status: ServiceProviderStatus
  credentials: CredentialsResponse
}

export const useFinch = (token: string): UseFinchReturn => {
  const [isLoadingConnectionStatus, setIsLoadingConnectionStatus] = useState(false)
  const [isLoadingSession, setIsLoadingSession] = useState(false)
  const [isLoadingReauthenticateSession, setIsLoadingReauthenticateSession] = useState(false)
  const [isHandlingSessionSuccess, setIsHandlingSessionSuccess] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  const getConnectionStatus = useCallback(
    async (params: Array<[string, string]>): Promise<CredentialsDTO> => {
      setIsLoadingConnectionStatus(true)
      setError(null)

      try {
        const response = await fetchWithToken('credentials/finch/status', token, params)

        return response as CredentialsDTO
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Failed to get Finch connection status')
        setError(error)
        setIsLoadingConnectionStatus(false)
        throw error
      } finally {
        setIsLoadingConnectionStatus(false)
      }
    },
    [token]
  )

  const getSession = useCallback(
    async (request: FinchSessionRequest): Promise<FinchSessionResponse> => {
      setIsLoadingSession(true)
      setError(null)

      try {
        const response = await postWithToken('credentials/finch/session', token, request)
        return response as FinchSessionResponse
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Failed to create Finch session')
        setError(error)
        throw error
      } finally {
        setIsLoadingSession(false)
      }
    },
    [token]
  )

  const getReauthenticateSession = useCallback(
    async (request: FinchReauthenticateSessionRequest): Promise<FinchReauthenticateSessionResponse> => {
      setIsLoadingReauthenticateSession(true)
      setError(null)

      try {
        const response = await postWithToken('credentials/finch/session/reauth', token, request)
        return response as FinchReauthenticateSessionResponse
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Failed to handle the reauthentication request')
        setError(error)
        throw error
      } finally {
        setIsLoadingReauthenticateSession(false)
      }
    },
    [token]
  )

  const handleSessionSuccess = useCallback(
    async (request: FinchSessionSuccessRequest): Promise<FinchSessionSuccessResponse> => {
      setIsHandlingSessionSuccess(true)
      setError(null)

      try {
        const response = await postWithToken('credentials/finch/session/success', token, request)
        return response as FinchSessionResponse
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Failed to handle the session success')
        setError(error)
        throw error
      } finally {
        setIsHandlingSessionSuccess(false)
      }
    },
    [token]
  )

  return {
    getConnectionStatus,
    isLoadingConnectionStatus,
    getSession,
    isLoadingSession,
    getReauthenticateSession,
    isLoadingReauthenticateSession,
    handleSessionSuccess,
    isHandlingSessionSuccess,
    error,
  }
}
